import React, { useState, useEffect } from 'react';
import { getContactInitials } from '../utils/contactInitials';
import { isContactAcquireur, isContactMandant, numeroWhatsapp } from '../utils/contactProfile';
import { libelleRole } from '../constants/contactEnums';
import useTelephone from '../hooks/useTelephone';
import TaskCreateModal from '../components/TaskCreateModal';
import LinkedTasksSection from '../components/LinkedTasksSection';
import EmailSendModal from '../components/EmailSendModal';
import EmailGeneratorModal from '../components/EmailGeneratorModal';
import AffectPropertyToContactModal from '../components/AffectPropertyToContactModal';
import MultipleSearchesManager from '../components/contact-form/MultipleSearchesManager';
import ProposeBienEmailModal from '../components/contact-view/ProposeBienEmailModal';
import { formatSurfaceRange } from '../utils/formatRanges';
import IdentityContactBlock from '../components/contact-view/IdentityContactBlock';
import FinancialProfileBlock from '../components/contact-view/FinancialProfileBlock';
import SegmentationBlock from '../components/contact-view/SegmentationBlock';
import { formatPriceFR, formatRentFR, formatSurfaceFR, formatPhoneFR, formatDateFR } from '../utils/formatFR';
import { getMediaUrl } from '../utils/mediaUrl';
import { toast } from 'sonner';
import api from '../services/api';
import { getAuthHeaders } from '../utils/auth';
import ContactCascadeModal from '../components/contacts/ContactCascadeModal';
import DocumentsInternesPanel from '../components/documents/DocumentsInternesPanel';

// Trie l'historique du plus récent au plus ancien
const sortHistDesc = (arr) =>
  [...(arr || [])].sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// lot DE — onglet courant lu dans l'URL (`?onglet=resume|recherche|biens|activite`)
const ONGLETS_VALIDES = ['resume', 'recherche', 'biens', 'activite'];
const lireOngletUrl = () => {
  const demande = new URLSearchParams(window.location.search).get('onglet');
  return ONGLETS_VALIDES.includes(demande) ? demande : 'resume';
};

const ContactViewMode = ({ contact: initialContact, onEdit, onClose, onArchive, onDelete, userProfile }) => {
  const [contact, setContact] = useState(initialContact);
  const [aiSummary, setAiSummary] = useState(null);
  const [matchingCount, setMatchingCount] = useState(0);
  const [matchesByRecherche, setMatchesByRecherche] = useState({});
  const [matchingProperties, setMatchingProperties] = useState([]);
  const [showMatchingModal, setShowMatchingModal] = useState(false);
  const [expandedMatchRecherche, setExpandedMatchRecherche] = useState(null);
  const [editingSearches, setEditingSearches] = useState(false);
  const [searchesDraft, setSearchesDraft] = useState([]);
  const [savingSearches, setSavingSearches] = useState(false);
  const [affectedProperties, setAffectedProperties] = useState([]);
  const [emailBien, setEmailBien] = useState(null); // { id, reference } pour la modale email de proposition
  const [presentedProperties, setPresentedProperties] = useState([]);
  const [visites, setVisites] = useState([]);
  const [ownedProperties, setOwnedProperties] = useState([]);
  const [history, setHistory] = useState([]);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [cascadeModal, setCascadeModal] = useState({ open: false, mode: 'archive', linkedProperties: [], hasActiveSearches: false, isAcquereur: false, isProprietaire: false });
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAIEmailModal, setShowAIEmailModal] = useState(false);
  const [showAffectModal, setShowAffectModal] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [expandedHistoryItem, setExpandedHistoryItem] = useState(null);
  const [assigningPropertyId, setAssigningPropertyId] = useState(null);
  const [removingPropertyId, setRemovingPropertyId] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [refreshingMatching, setRefreshingMatching] = useState(false);
  // lot DE — téléphone : quatre onglets, l'onglet courant vit dans l'URL (`?onglet=`)
  const telephone = useTelephone();
  const [onglet, setOnglet] = useState(() => lireOngletUrl());
  const [resumeIaDeplie, setResumeIaDeplie] = useState(false);
  const [prochaineTache, setProchaineTache] = useState(null);
  const [rechercheOuverteId, setRechercheOuverteId] = useState(null);
  const [negociateurNom, setNegociateurNom] = useState(null);

  useEffect(() => {
    const surRetour = () => setOnglet(lireOngletUrl());
    window.addEventListener('popstate', surRetour);
    return () => window.removeEventListener('popstate', surRetour);
  }, []);

  // « À suivre » (téléphone) : la prochaine tâche non terminée, même route que les tâches liées
  useEffect(() => {
    if (!telephone || !contact?.id) return;
    let annule = false;
    api.get('/tasks', { params: { linked_contact_id: contact.id } })
      .then(({ data }) => {
        if (annule) return;
        const aFaire = (data || [])
          .filter((t) => t.status !== 'done')
          .sort((a, b) => new Date(a.due_date || '2999-12-31') - new Date(b.due_date || '2999-12-31'));
        setProchaineTache(aFaire[0] || null);
      })
      .catch(() => setProchaineTache(null));
    return () => { annule = true; };
  }, [telephone, contact?.id]);

  // lot DE n°1 — « Suivi par » : le nom du négociateur, résolu depuis `negociateur_id`
  // (route existante des négociateurs de l'agence, aucune route nouvelle).
  useEffect(() => {
    if (!telephone || !contact?.negociateur_id) {
      setNegociateurNom(null);
      return;
    }
    let annule = false;
    api.get('/users/negotiators')
      .then(({ data }) => {
        if (annule) return;
        const n = (data || []).find((u) => u.id === contact.negociateur_id);
        setNegociateurNom(
          n ? `${n.prenom || ''} ${n.nom || ''}`.trim() || n.email || null : null,
        );
      })
      .catch(() => setNegociateurNom(null));
    return () => { annule = true; };
  }, [telephone, contact?.negociateur_id]);


  const changerOnglet = (id) => {
    const url = new URL(window.location.href);
    url.searchParams.set('onglet', id);
    window.history.pushState({}, '', url);
    setOnglet(id);
  };

  // Sync contact si la prop change
  useEffect(() => {
    setContact(initialContact);
  }, [initialContact]);

  // Charger les données supplémentaires
  useEffect(() => {
    if (contact?.id) {
      if (contact.resume_ia?.texte) {
        setAiSummary(contact.resume_ia.texte);
      } else {
        setAiSummary(null);
      }
      setHistory(sortHistDesc(contact.historique_communications || []));
      loadPresentedProperties();
      loadVisites();
      loadOwnedProperties();
      fetchMatchingProperties();
    }
  }, [contact?.id]);

  // lot DA — registre UNIQUE : la collection des présentations. « Affectés » = statut
  // « sélectionné », « Présentés » = « présenté » ou « visité » (bon de visite / visite faite).
  const loadPresentedProperties = async () => {
    if (!contact?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/biens-presentes`, { headers: getAuthHeaders() });
      if (!response.ok) {
        setAffectedProperties([]);
        setPresentedProperties([]);
        return;
      }
      const presentations = await response.json();
      setAffectedProperties(presentations.filter((p) => !['présenté', 'visité'].includes(p.statut)));
      setPresentedProperties(presentations.filter((p) => ['présenté', 'visité'].includes(p.statut)));
    } catch (err) {
      console.error('Erreur chargement biens liés:', err);
      setAffectedProperties([]);
      setPresentedProperties([]);
    }
  };

  const loadVisites = async () => {
    if (!contact?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/visites`, { headers: getAuthHeaders() });
      setVisites(response.ok ? await response.json() : []);
    } catch (err) {
      console.error('Erreur chargement visites:', err);
      setVisites([]);
    }
  };

  const loadOwnedProperties = async () => {
    if (!contact?.id) return;
    try {
      const { data } = await api.get(`/contacts/${contact.id}/linked-properties`);
      setOwnedProperties(data.linked_properties || []);
    } catch (err) {
      console.error('Erreur chargement biens en portefeuille:', err);
      setOwnedProperties([]);
    }
  };

  const fetchMatchingProperties = async () => {
    if (!contact?.id) return;
    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(`${API_URL}/api/contacts/matching-v2/${contact.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        if (data.matches_by_recherche) {
          setMatchesByRecherche(data.matches_by_recherche);
          const allMatches = [];
          Object.values(data.matches_by_recherche).forEach((r) => {
            if (r.scored_matches?.length) allMatches.push(...r.scored_matches);
          });
          const seen = new Map();
          allMatches.forEach((m) => {
            const id = m.property?.id || m.property?.reference;
            if (!id) return;
            // lot DF — un bien trouvé par plusieurs recherches : on garde le MEILLEUR score
            const existant = seen.get(id);
            if (!existant || (m.score || 0) > (existant.score || 0)) seen.set(id, m);
          });
          const unique = Array.from(seen.values());
          unique.sort((a, b) => (b.score || 0) - (a.score || 0));
          setMatchingCount(unique.length);
          setMatchingProperties(unique);
        } else {
          const list = data.scored_matches || [];
          setMatchingCount(data.total || list.length || 0);
          setMatchingProperties(list);
        }
      }
    } catch (err) {
      console.error('Erreur récupération matching:', err);
    }
  };

  const handleRefreshMatching = async () => {
    setRefreshingMatching(true);
    try {
      await fetchMatchingProperties();
      toast.success('Rapprochement relancé');
    } finally {
      setRefreshingMatching(false);
    }
  };


  // ========== SAVE INLINE EDIT ==========
  const handleInlineSave = async (partialUpdate) => {
    if (!contact?.id) throw new Error('Contact ID manquant');
    const token = localStorage.getItem('token') || '';
    const response = await fetch(`${API_URL}/api/contacts/${contact.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(partialUpdate),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    const updated = await response.json();
    setContact((prev) => ({ ...prev, ...updated }));
    toast.success('Modifications enregistrées');
  };

  // ========== RECHARGER LE CONTACT (après email IA historisé, etc.) ==========
  const reloadContact = async () => {
    if (!contact?.id) return;
    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const updated = await response.json();
        setContact(updated);
        setHistory(sortHistDesc(updated.historique_communications || []));
      }
    } catch (err) {
      console.error('Erreur rechargement contact:', err);
    }
  };

  // ========== ÉDITION INLINE DES RECHERCHES ==========
  const startEditSearches = () => {
    setSearchesDraft(contact?.recherches ? JSON.parse(JSON.stringify(contact.recherches)) : []);
    setRechercheOuverteId(null);
    setEditingSearches(true);
  };

  // lot DE §4 — « Ajouter une recherche » : mode modification avec une recherche vide dépliée
  const startEditSearchesWithNew = () => {
    const existantes = contact?.recherches ? JSON.parse(JSON.stringify(contact.recherches)) : [];
    if (existantes.length >= 5) {
      toast.warning('5 recherches au maximum par contact');
      startEditSearches();
      return;
    }
    const nouvelle = {
      id: `r${Date.now()}`,
      priorite: existantes.length + 1,
      label: `Recherche ${existantes.length + 1}`,
      active: true,
      operation: null,
      profil: null,
      sous_activites: [],
      villes: [],
      secteurs: [],
      budget_min: null,
      budget_max: null,
      droit_entree_min: null,
      droit_entree_max: null,
      surface_min: null,
      surface_max: null,
      equipements_souhaites: {},
      notes: '',
      date_creation: new Date().toISOString(),
    };
    setSearchesDraft([...existantes, nouvelle]);
    setRechercheOuverteId(nouvelle.id);
    setEditingSearches(true);
  };

  const handleSaveSearches = async () => {
    setSavingSearches(true);
    try {
      await handleInlineSave({ recherches: searchesDraft });
      setEditingSearches(false);
      // Recalculer le matching après modification des recherches
      fetchMatchingProperties();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'enregistrement des recherches');
    } finally {
      setSavingSearches(false);
    }
  };

  // ========== AFFECTATIONS ==========
  const handleAssignProperty = async (propertyId, propertyReference) => {
    if (!contact?.id || !propertyId) return;
    setAssigningPropertyId(propertyId);
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/biens-presentes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          bien_id: propertyId,
          negociateur_id: userProfile?.id || userProfile?.userId || 'default_user',
          statut: 'sélectionné',
        }),
      });
      if (response.ok) {
        await loadPresentedProperties();
        toast.success(`Bien "${propertyReference}" affecté`);
      } else {
        const errorData = await response.json();
        if (errorData.detail?.includes('déjà affecté')) {
          toast.warning('Ce bien est déjà affecté à ce contact');
        } else {
          toast.error(errorData.detail || 'Impossible d\'affecter le bien');
        }
      }
    } catch (error) {
      console.error('Erreur affectation bien:', error);
      toast.error(error.message);
    } finally {
      setAssigningPropertyId(null);
    }
  };

  const handleRemoveAffectation = async (presentationId, propertyReference) => {
    if (!presentationId) return;
    if (!window.confirm(`Supprimer l'affectation du bien "${propertyReference}" ?`)) return;
    setRemovingPropertyId(presentationId);
    try {
      const response = await fetch(`${API_URL}/api/contacts/biens-presentes/${presentationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        await loadPresentedProperties();
        await fetchMatchingProperties();
        toast.success('Affectation supprimée');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Impossible de supprimer');
      }
    } catch (error) {
      console.error('Erreur suppression affectation:', error);
      toast.error(error.message);
    } finally {
      setRemovingPropertyId(null);
    }
  };

  // ========== ACTIONS ==========
  const handlePrint = () => {
    window.open(`/contacts/${contact.id}/fiche`, '_blank');
  };

  const handleArchive = async () => {
    try {
      const { data: links } = await api.get(`/contacts/${contact.id}/linked-properties`);
      if (links.count > 0) {
        setCascadeModal({
          open: true,
          mode: 'archive',
          linkedProperties: links.linked_properties,
          hasActiveSearches: links.has_active_searches,
          isAcquereur: links.is_acquereur,
          isProprietaire: links.is_proprietaire,
        });
        return;
      }
      if (!window.confirm('Êtes-vous sûr de vouloir archiver ce contact ?')) return;
      await api.post(`/contacts/${contact.id}/archive`, {});
      toast.success('Contact archivé');
      if (onArchive) onArchive(contact.id);
      onClose();
    } catch (error) {
      console.error('Erreur archivage:', error);
      toast.error("Erreur lors de l'archivage");
    }
  };

  const confirmCascadeArchive = async (propertyIds) => {
    try {
      await api.post(`/contacts/${contact.id}/archive`, { cascade_property_ids: propertyIds });
      setCascadeModal((m) => ({ ...m, open: false }));
      toast.success(propertyIds.length > 0 ? `Contact + ${propertyIds.length} bien(s) archivé(s)` : 'Contact archivé');
      if (onArchive) onArchive(contact.id);
      onClose();
    } catch (error) {
      console.error('Erreur archivage cascade:', error);
      toast.error("Erreur lors de l'archivage en cascade");
    }
  };

  const handleDelete = async () => {
    try {
      const { data: links } = await api.get(`/contacts/${contact.id}/linked-properties`);
      if (links.count > 0) {
        setCascadeModal({
          open: true,
          mode: 'delete-blocked',
          linkedProperties: links.linked_properties,
          hasActiveSearches: links.has_active_searches,
          isAcquereur: links.is_acquereur,
          isProprietaire: links.is_proprietaire,
        });
        return;
      }
      if (!window.confirm('Êtes-vous sûr de vouloir SUPPRIMER définitivement ce contact ?')) return;
      await api.delete(`/contacts/${contact.id}`);
      toast.success('Contact supprimé');
      if (onDelete) onDelete(contact.id);
      onClose();
    } catch (error) {
      if (error.response?.status === 409) {
        const d = error.response.data?.detail || {};
        toast.error(d.message || 'Suppression impossible : biens actifs liés.');
      } else {
        console.error('Erreur suppression:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleRegenerateAiSummary = async () => {
    if (!contact?.id) return;
    setRegenerating(true);
    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/generate-summary`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const text = data?.resume_ia?.texte || data?.texte || data?.summary || null;
        if (text) {
          setAiSummary(text);
          setContact((prev) => ({
            ...prev,
            resume_ia: { texte: text, date_generation: new Date().toISOString(), stale: false },
          }));
          toast.success('Résumé IA régénéré');
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Régénération impossible');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRegenerating(false);
    }
  };

  // ========== SCORE & RATIO ==========
  const calculateScore = () => {
    const apport = parseFloat(contact?.informations_confidentielles?.apport_disponible) || 0;
    const capacite = parseFloat(contact?.informations_confidentielles?.capacite_emprunt) || 0;
    const hasFinances = apport > 0 && capacite > 0;
    const hasBudget = !!contact?.criteres_recherche?.budget_max;
    const hasTypes = (contact?.criteres_recherche?.types_biens || []).length > 0;
    const hasVilles = (contact?.criteres_recherche?.villes || []).length > 0;
    if (!hasFinances && !hasBudget && !hasTypes && !hasVilles) return null;
    let score = 50;
    if (hasFinances) score += 20;
    if (hasBudget) score += 10;
    if (hasTypes) score += 10;
    if (hasVilles) score += 10;
    return Math.min(score, 100);
  };

  const score = calculateScore();
  const displayName = `${contact?.titre || ''} ${contact?.prenom || ''} ${contact?.nom || 'Contact'}`.trim();

  // Helpers
  const formatOperation = (operation) => {
    const map = {
      cession: 'Achat',
      location: 'Location',
      location_droit_entree: 'Loc. pas-de-porte',
      location_gerance: 'Loc.-gérance',
      vente_immo: 'Vente murs',
      achat: 'Achat',
    };
    return map[operation] || operation;
  };

  // Libellé "type de bien" pour l'affichage des biens correspondants (côté bien, pas côté recherche)
  const PROFIL_LABELS = {
    chr: 'CHR',
    retail: 'Commerce',
    bureaux: 'Bureaux',
    activite_logistique: 'Activité / Logistique',
  };
  const OPERATION_BIEN_LABELS = {
    cession: 'Cession de fonds',
    vente_immo: 'Vente des murs',
    location: 'Location',
    location_droit_entree: 'Location + pas-de-porte',
    location_gerance: 'Location-gérance',
  };
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const formatBienType = (property) => {
    if (!property) return '';
    const op = OPERATION_BIEN_LABELS[property.operation] || '';
    const objet = property.objet ? cap(property.objet.replace(/_/g, ' ')) : '';
    const profil = PROFIL_LABELS[property.profil] || cap(property.profil);
    const sous = property.sous_profil ? cap(property.sous_profil.replace(/_/g, ' ')) : '';
    // Ex: "Cession de fonds · CHR · Restaurant"  (on évite de répéter objet si déjà couvert par l'opération)
    return [op, profil, sous].filter(Boolean).join(' · ') || objet;
  };

  const getBienDisplayData = (wrapper) => {
    const bien = wrapper.property || wrapper;
    const matchScore = wrapper.score || null;
    const refBrute = bien.reference_interne || bien.reference || bien.numero_mandat || null;
    const typeBien = bien.objet || bien.type_bien || '';
    let reference = '';
    if (refBrute && typeBien) {
      reference = `${refBrute} - ${typeBien.charAt(0).toUpperCase() + typeBien.slice(1)}`;
    } else if (refBrute) reference = refBrute;
    else if (typeBien) reference = typeBien.charAt(0).toUpperCase() + typeBien.slice(1);
    else reference = 'Bien sans référence';
    const ville =
      bien.informations_confidentielles?.ville ||
      bien.public_location?.city ||
      bien.adresse_bien?.ville ||
      'Ville inconnue';
    const operation = bien.operation || null;
    let prix = null,
      loyer = null,
      droitEntree = null;
    if (['location', 'location_droit_entree', 'location_gerance'].includes(operation)) {
      loyer = bien.informations_bail?.loyer_mensuel;
      prix = loyer;
      if (operation === 'location_droit_entree') {
        droitEntree = bien.informations_bail?.droit_entree || bien.informations_bail?.pas_de_porte;
      }
    } else {
      prix = bien.pricing?.prix_de_vente || bien.pricing?.prix_cession || bien.prix_demande || bien.informations_vente?.prix_annonce || bien.honoraires_calcul?.prix_vente || bien.informations_vente?.prix_cession || bien.prix_de_presentation;
    }
    const surface =
      bien.composition?.surface_totale || bien.composition?.superficie_totale || bien.surface_bien || null;
    // Photo : passer par helper pour appliquer auth (?auth=) et déduire la variante thumb
    const photoObj = bien.medias?.photos?.[0] || bien.photos?.[0] || null;
    const photo = photoObj ? getMediaUrl(photoObj, 'thumb') : null;
    return { reference, ville, prix, loyer, droitEntree, operation, surface, photo, id: bien.id, matchScore, status: bien.status, overBudget: wrapper.over_budget || false, overBudgetPct: wrapper.over_budget_pct || 0 };
  };

  const formatEventTitle = (event) => {
    const emailSubject = event.subject || event.sujet;
    if (emailSubject && (event.type === 'email' || event.type === 'email_ia_genere' || event.type === 'email_recu')) {
      return `${emailSubject}`;
    }
    if (event.type === 'document') return `${event.label || 'Document'}`;
    if (event.titre && event.titre !== event.type) return event.titre;
    if (emailSubject) return emailSubject;
    const map = {
      email: 'Email envoyé',
      email_ia_genere: 'Email IA généré',
      email_recu: 'Email reçu',
      appel: 'Appel téléphonique',
      visite: 'Visite de bien',
      rdv: 'Rendez-vous',
      note: 'Note ajoutée',
      tache: 'Tâche',
      sms: 'SMS',
      creation: 'Contact créé',
      modification: 'Contact modifié',
      document: 'Document',
    };
    return map[event.type] || event.type || 'Événement';
  };

  const getEventContent = (event) => event.body || event.contenu || event.description || null;

  const getMainPhone = () => {
    const phones = contact?.telephones || [];
    return (phones.find((p) => p.principal) || phones[0])?.numero || null;
  };
  const getMainEmail = () => {
    const emails = contact?.emails || [];
    return (emails.find((e) => e.principal) || emails[0])?.email || null;
  };

  // ========== RENDER ==========
  // Biens déjà affectés au contact (pour l'état du bouton "Affecter ce bien")
  const affectedBienIds = new Set(
    (affectedProperties || []).map((p) => getBienDisplayData(p).id).filter(Boolean)
  );
  const renderAffectBtn = (bienId, reference) => {
    const already = affectedBienIds.has(bienId);
    return (
      <button
        type="button"
        data-testid={`affect-matched-${bienId}`}
        disabled={already || assigningPropertyId === bienId}
        onClick={(e) => {
          e.stopPropagation();
          if (!already) handleAssignProperty(bienId, reference);
        }}
        className={`shrink-0 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition ${
          telephone ? 'min-h-[44px] min-w-[44px] px-3' : 'px-2.5 py-1.5'
        } ${
          already
            ? 'bg-emerald-50 text-emerald-600 cursor-default'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
        title={already ? 'Déjà affecté à ce contact' : 'Affecter ce bien au contact'}
      >
        {assigningPropertyId === bienId ? (
          <i className="bi bi-arrow-repeat animate-spin"></i>
        ) : already ? (
          <>
            <i className="bi bi-check2"></i> Affecté
          </>
        ) : (
          <>
            <i className="bi bi-plus-lg"></i> Affecter
          </>
        )}
      </button>
    );
  };

  // lot DE — chaque bloc devient une valeur : l'ordinateur les empile comme avant, le
  // téléphone les range dans quatre onglets. Aucun bloc n'est réécrit.
  const enTete = (
        <header className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            {/* Gauche: Retour + Photo + Infos */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                type="button"
                onClick={onClose}
                data-testid="contact-back-btn"
                className="w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center shrink-0"
                title="Retour"
              >
                <i className="bi bi-arrow-left text-lg"></i>
              </button>

              <div className="w-16 h-16 rounded-full bg-blue-100 text-[#007BFF] flex items-center justify-center font-bold text-xl shrink-0 ring-2 ring-blue-50 overflow-hidden">
                {contact?.photo_url ? (
                  <img
                    src={getMediaUrl(contact.photo_url)}
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getContactInitials(contact?.prenom, contact?.nom)
                )}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate" data-testid="contact-name">
                    {displayName}
                  </h1>
                  {contact?.type_contact && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[11px] font-bold uppercase">
                      {contact.type_contact}
                    </span>
                  )}
                  {contact?.statut_lead && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md text-[11px] font-bold uppercase">
                      {contact.statut_lead}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                  {getMainPhone() && (
                    <span className="num">
                      <i className="bi bi-telephone mr-1"></i>
                      {formatPhoneFR(getMainPhone())}
                    </span>
                  )}
                  {getMainEmail() && (
                    <span>
                      <i className="bi bi-envelope mr-1"></i>
                      {getMainEmail()}
                    </span>
                  )}
                  {contact?.ville && (
                    <span>
                      <i className="bi bi-geo-alt mr-1"></i>
                      {contact.ville}
                    </span>
                  )}
                  {contact?.created_at && (
                    <span className="num">
                      <i className="bi bi-calendar3 mr-1"></i>
                      Créé le {formatDateFR(contact.created_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Droite: Icônes d'action + Email IA + Modifier */}
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
              {getMainPhone() && (
                <a
                  href={`tel:${getMainPhone()}`}
                  data-testid="contact-tel-btn"
                  className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                  title="Appeler"
                >
                  <i className="bi bi-telephone text-lg"></i>
                </a>
              )}
              {getMainPhone() && (
                <a
                  href={`https://wa.me/${getMainPhone().replace(/\s/g, '').replace(/^0/, '33')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-wa-btn"
                  className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                  title="WhatsApp"
                >
                  <i className="bi bi-whatsapp text-lg"></i>
                </a>
              )}
              <button
                type="button"
                onClick={() => setShowEmailModal(true)}
                data-testid="contact-email-btn"
                className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                title="Envoyer un email"
              >
                <i className="bi bi-envelope text-lg"></i>
              </button>
              <button
                type="button"
                onClick={() => setShowAIEmailModal(true)}
                data-testid="contact-ai-email-btn"
                className="w-10 h-10 rounded-lg text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center justify-center transition"
                title="Rédiger un email avec l'IA"
              >
                <i className="bi bi-envelope-paper-heart text-lg"></i>
              </button>
              <button
                type="button"
                onClick={() => setShowTaskModal(true)}
                data-testid="contact-task-btn"
                className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                title="Tâche"
              >
                <i className="bi bi-check2-square text-lg"></i>
              </button>
              <button
                type="button"
                onClick={() => (window.location.href = `/visites/nouvelle?contact=${contact.id}&retour=/contacts/${contact.id}`)}
                data-testid="contact-visite-btn"
                className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                title="Visite"
              >
                <i className="bi bi-door-open text-lg"></i>
              </button>
              <button
                type="button"
                onClick={() => setShowEventModal(true)}
                data-testid="contact-rdv-btn"
                className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                title="RDV"
              >
                <i className="bi bi-calendar-event text-lg"></i>
              </button>
              <button
                type="button"
                onClick={handlePrint}
                data-testid="contact-print-btn"
                className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                title="Imprimer"
              >
                <i className="bi bi-printer text-lg"></i>
              </button>

              {/* Menu trois points */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  data-testid="contact-more-btn"
                  className="w-10 h-10 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
                  title="Plus d'actions"
                >
                  <i className="bi bi-three-dots-vertical text-lg"></i>
                </button>
                {showActionsMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowActionsMenu(false)}></div>
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                      <button
                        type="button"
                        onClick={() => {
                          handleArchive();
                          setShowActionsMenu(false);
                        }}
                        data-testid="contact-archive-btn"
                        className="w-full px-4 py-2.5 text-left text-sm font-medium text-orange-500 hover:bg-orange-50 flex items-center gap-3"
                      >
                        <i className="bi bi-archive-fill"></i> Archiver
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleDelete();
                          setShowActionsMenu(false);
                        }}
                        data-testid="contact-delete-btn"
                        className="w-full px-4 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3"
                      >
                        <i className="bi bi-trash-fill"></i> Supprimer
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
  );

  const sectionKpis = isContactAcquireur(contact) && (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="contact-kpis">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Score Faisabilité
              </p>
              <p className="text-3xl font-bold text-slate-900 num">
                {score === null ? '—' : score}
                <span className="text-base text-slate-400 font-medium">/100</span>
              </p>
              <p
                className={`text-xs font-semibold mt-1 ${
                  score === null
                    ? 'text-slate-500'
                    : score >= 80
                    ? 'text-emerald-700'
                    : score >= 50
                    ? 'text-amber-700'
                    : 'text-red-700'
                }`}
              >
                {score === null
                  ? 'Données insuffisantes'
                  : score >= 80
                  ? 'Excellent'
                  : score >= 50
                  ? 'À consolider'
                  : 'Incomplet'}
              </p>
            </div>
            <div
              data-testid="contact-matching-count-card"
              onClick={() => matchingCount > 0 && setShowMatchingModal(true)}
              className={`relative w-full text-left bg-white rounded-xl shadow-sm border border-slate-200 p-4 transition ${matchingCount > 0 ? 'hover:border-blue-300 hover:shadow-md cursor-pointer' : 'cursor-default'}`}
            >
              <button
                type="button"
                data-testid="contact-refresh-matching-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefreshMatching();
                }}
                disabled={refreshingMatching}
                title="Relancer le matching"
                className="absolute top-3 right-3 w-7 h-7 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center transition disabled:opacity-50"
              >
                <i className={`bi bi-arrow-clockwise ${refreshingMatching ? 'animate-spin' : ''}`}></i>
              </button>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Matchs portefeuille
              </p>
              <p className="text-3xl font-bold text-slate-900 num">{matchingCount}</p>
              <p className="text-[11px] text-blue-600 font-semibold mt-1 flex items-center gap-1">
                {matchingCount > 0 ? 'voir les biens' : 'biens correspondent'}
                {matchingCount > 0 && <i className="bi bi-arrow-right-circle"></i>}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Biens proposés
              </p>
              <p className="text-3xl font-bold text-slate-900 num">
                {affectedProperties.length + presentedProperties.length}
              </p>
              <p className="text-[11px] text-slate-600 mt-1">
                {affectedProperties.length} affecté{affectedProperties.length > 1 ? 's' : ''} ·{' '}
                {presentedProperties.length} présenté{presentedProperties.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Dernière interaction
              </p>
              <p className="text-xl font-bold text-slate-900 num">
                {history.length > 0 && history[0].date
                  ? formatDateFR(history[0].date)
                  : '—'}
              </p>
              <p className="text-[11px] text-slate-500 mt-1 num">
                {history.length} événement{history.length > 1 ? 's' : ''}
              </p>
            </div>
          </section>
  );

  const sectionResumeIA = aiSummary && (
          <section
            className="rounded-xl shadow-md text-white p-5"
            style={{
              background: 'linear-gradient(135deg, #7C3AED 0%, #4338CA 100%)',
            }}
            data-testid="contact-ai-summary"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="bi bi-stars text-white"></i>
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-white">Résumé IA</h2>
              <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/20 text-white border border-white/30">
                Komerces IA
              </span>
              {contact?.resume_ia?.stale && (
                <span
                  data-testid="ai-summary-stale-badge"
                  className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-200 border border-amber-300/40"
                  title="Les données du contact ont changé depuis la génération"
                >
                  <i className="bi bi-exclamation-triangle-fill mr-1"></i>À actualiser
                </span>
              )}
              <span className="text-[10px] text-violet-200 ml-auto num">
                {contact?.resume_ia?.date_generation
                  ? `Généré le ${formatDateFR(contact.resume_ia.date_generation)}`
                  : 'Généré récemment'}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-violet-50 whitespace-pre-wrap">{aiSummary}</p>
            <button
              type="button"
              onClick={handleRegenerateAiSummary}
              disabled={regenerating}
              data-testid="contact-regenerate-ai"
              className="mt-3 text-[11px] text-violet-200 hover:text-white font-semibold disabled:opacity-50"
            >
              <i className={`bi bi-arrow-clockwise mr-1 ${regenerating ? 'animate-spin' : ''}`}></i>
              {regenerating ? 'Régénération…' : 'Régénérer'}
            </button>
          </section>
  );

  const blocIdentite = <IdentityContactBlock contact={contact} onSave={handleInlineSave} />;
  const blocFinancier = <FinancialProfileBlock contact={contact} onSave={handleInlineSave} />;
  const blocSegmentation = <SegmentationBlock contact={contact} onSave={handleInlineSave} />;

  const sectionRecherches = isContactAcquireur(contact) && (
          <section
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
            data-testid="contact-searches"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 flex items-center justify-between">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <i className="bi bi-search"></i>
                Ce que recherche le client
              </h2>
              <div className="flex items-center gap-2">
                {!editingSearches && (
                  <button
                    type="button"
                    onClick={startEditSearchesWithNew}
                    data-testid="searches-add-btn"
                    className="h-8 px-3 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-semibold flex items-center gap-1.5 transition"
                    title="Ajouter une recherche"
                  >
                    <i className="bi bi-plus-lg"></i> Ajouter une recherche
                  </button>
                )}
                <button
                  type="button"
                  onClick={editingSearches ? () => setEditingSearches(false) : startEditSearches}
                  data-testid="searches-edit-toggle"
                  className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
                  title={editingSearches ? 'Annuler la modification' : 'Modifier les recherches'}
                >
                  {editingSearches ? <i className="bi bi-x-lg"></i> : <i className="bi bi-pencil-square"></i>}
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {editingSearches ? (
                <div data-testid="searches-edit-mode">
                  <MultipleSearchesManager
                    recherches={searchesDraft}
                    onUpdate={setSearchesDraft}
                    rechercheOuverteId={rechercheOuverteId}
                  />
                  <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setEditingSearches(false)}
                      disabled={savingSearches}
                      data-testid="searches-cancel-btn"
                      className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveSearches}
                      disabled={savingSearches}
                      data-testid="searches-save-btn"
                      className="px-4 py-2 bg-[#0062CC] hover:bg-[#0056b3] text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
                    >
                      {savingSearches ? (
                        <>
                          <i className="bi bi-arrow-repeat animate-spin"></i>
                          Enregistrement…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg"></i>
                          Enregistrer les recherches
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
              {contact?.recherches && contact.recherches.length > 0 ? (
                contact.recherches.map((recherche, idx) => {
                  const rechercheMatches = matchesByRecherche[recherche.id]?.scored_matches || [];
                  const isPrincipal = recherche.priorite === 1 || idx === 0;
                  return (
                    <div
                      key={recherche.id || idx}
                      data-testid={`recherche-${idx}`}
                      className={`rounded-xl p-4 ${
                        recherche.statut === 'pause'
                          ? 'border border-slate-200 bg-slate-50 opacity-70'
                          : isPrincipal
                          ? 'border-2 border-blue-200 bg-blue-50/50'
                          : 'border border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`w-7 h-7 rounded-lg ${
                              isPrincipal ? 'bg-blue-600' : 'bg-slate-400'
                            } text-white font-bold text-sm flex items-center justify-center`}
                          >
                            {idx + 1}
                          </span>
                          <h3 className="font-bold text-slate-900">
                            {recherche.label || `Recherche ${idx + 1}`}
                          </h3>
                          {recherche.operation && (
                            <span
                              className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                                recherche.operation === 'achat' || recherche.operation === 'cession'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {formatOperation(recherche.operation)}
                            </span>
                          )}
                          {recherche.statut === 'pause' && (
                            <span className="px-2 py-0.5 bg-slate-400 text-white rounded-md text-[10px] font-bold uppercase">
                              <i className="bi bi-pause-fill"></i> En pause
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 mb-3">
                        {(recherche.budget_min || recherche.budget_max) && (
                          <div className="bg-white rounded-lg p-2.5 text-center">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                              Budget
                            </p>
                            <p className="text-base font-bold text-slate-900 num mt-1">
                              {recherche.budget_min ? formatPriceFR(recherche.budget_min) : ''}
                              {recherche.budget_min && recherche.budget_max ? ' — ' : ''}
                              {recherche.budget_max ? formatPriceFR(recherche.budget_max) : ''}
                            </p>
                          </div>
                        )}
                        {(recherche.surface_min || recherche.surface_max) &&
                          (() => {
                            const lbl = formatSurfaceRange(recherche.surface_min, recherche.surface_max);
                            if (!lbl) return null;
                            return (
                              <div className="bg-white rounded-lg p-2.5 text-center">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                  Surface
                                </p>
                                <p className="text-base font-bold text-slate-900 num mt-1">{lbl}</p>
                              </div>
                            );
                          })()}
                        {recherche.profil && (
                          <div className="bg-white rounded-lg p-2.5 text-center">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                              Profil
                            </p>
                            <p className="text-sm font-bold text-slate-900 mt-1 capitalize">
                              {recherche.profil}
                            </p>
                          </div>
                        )}
                      </div>

                      {recherche.villes?.length > 0 && (
                        <div className="text-center mb-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            Secteurs
                          </p>
                          <div className="flex flex-wrap justify-center gap-1.5">
                            {recherche.villes.map((v, vi) => (
                              <span
                                key={vi}
                                className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[11px] font-semibold"
                              >
                                <i className="bi bi-geo-alt-fill"></i> {v}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {recherche.notes && (
                        <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 mt-2">
                          <p className="font-semibold text-slate-600 mb-1">Critères additionnels</p>
                          {recherche.notes}
                        </div>
                      )}

                      {rechercheMatches.length > 0 && (
                        <div className="mt-3">
                          <button
                            type="button"
                            data-testid={`recherche-matches-toggle-${idx}`}
                            onClick={() =>
                              setExpandedMatchRecherche(
                                expandedMatchRecherche === (recherche.id || idx)
                                  ? null
                                  : recherche.id || idx
                              )
                            }
                            className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-lg py-2 transition"
                          >
                            <i className="bi bi-buildings"></i>
                            <span>
                              <strong>{rechercheMatches.length}</strong> bien
                              {rechercheMatches.length > 1 ? 's' : ''} correspondant
                              {rechercheMatches.length > 1 ? 's' : ''}
                            </span>
                            <i
                              className={`bi ${
                                expandedMatchRecherche === (recherche.id || idx)
                                  ? 'bi-chevron-up'
                                  : 'bi-chevron-down'
                              }`}
                            ></i>
                          </button>
                          {expandedMatchRecherche === (recherche.id || idx) && (
                            <ul
                              className="mt-2 space-y-2"
                              data-testid={`recherche-matches-list-${idx}`}
                            >
                              {[...rechercheMatches]
                                .sort((a, b) => (b.score || 0) - (a.score || 0))
                                .map((m, mi) => {
                                  const d = getBienDisplayData(m);
                                  const isLoc = [
                                    'location',
                                    'location_droit_entree',
                                    'location_gerance',
                                  ].includes(d.operation);
                                  const sc = m.score || 0;
                                  return (
                                    <li
                                      key={`rmatch-${idx}-${mi}`}
                                      data-testid={`recherche-match-${idx}-${mi}`}
                                      onClick={() =>
                                        d.id && (window.location.href = `/properties/${d.id}/view`)
                                      }
                                      className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer transition"
                                    >
                                      <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0 overflow-hidden">
                                        {d.photo ? (
                                          <img
                                            src={d.photo}
                                            alt={d.reference}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <i className="bi bi-image"></i>
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0 text-left">
                                        <p className="text-[11px] font-bold text-slate-500 num truncate flex items-center gap-1.5">
                                          {d.reference}
                                          {d.status === 'draft' && (
                                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-bold uppercase tracking-wide shrink-0">
                                              Brouillon
                                            </span>
                                          )}
                                          {d.overBudget && (
                                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-bold uppercase tracking-wide shrink-0" title="Léger dépassement du budget">
                                              +{d.overBudgetPct}% budget
                                            </span>
                                          )}
                                        </p>
                                        {formatBienType(m.property) && (
                                          <p className="text-[11px] font-semibold text-blue-700 truncate">
                                            {formatBienType(m.property)}
                                          </p>
                                        )}
                                        <p className="text-xs text-slate-600 num truncate">
                                          {d.ville}
                                          {d.surface ? ` · ${formatSurfaceFR(d.surface)}` : ''}
                                          {isLoc
                                            ? d.loyer
                                              ? ` · ${formatRentFR(d.loyer)}`
                                              : ''
                                            : d.prix
                                            ? ` · ${formatPriceFR(d.prix)}`
                                            : ''}
                                          {d.droitEntree ? (
                                            <span className="text-amber-600 ml-1">
                                              +{formatPriceFR(d.droitEntree)}
                                            </span>
                                          ) : null}
                                        </p>
                                      </div>
                                      <span
                                        className={`shrink-0 px-2 py-1 rounded-md text-xs font-bold num ${
                                          sc >= 80
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : sc >= 50
                                            ? 'bg-amber-100 text-amber-700'
                                            : 'bg-slate-100 text-slate-600'
                                        }`}
                                      >
                                        {sc}%
                                      </span>
                                      {renderAffectBtn(d.id, d.reference)}
                                    </li>
                                  );
                                })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : contact?.criteres_recherche &&
                (contact.criteres_recherche?.budget_min ||
                  contact.criteres_recherche?.budget_max ||
                  contact.criteres_recherche?.villes?.length > 0) ? (
                <div className="rounded-xl border-2 border-blue-200 bg-white p-4">
                  <p className="text-sm text-slate-700">
                    <strong>Recherche principale</strong> ({formatOperation(contact.criteres_recherche.operation)})
                  </p>
                  {contact.criteres_recherche.villes?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {contact.criteres_recherche.villes.map((v, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[11px] font-semibold"
                        >
                          <i className="bi bi-geo-alt-fill"></i> {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic text-center py-4">
                  Aucun critère de recherche défini. Cliquez sur "Modifier" pour en ajouter.
                </p>
              )}
                </>
              )}
            </div>
          </section>
  );

  const sectionBiensProprietaire = ownedProperties.length > 0 && (
            <section
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              data-testid="contact-owned-properties"
            >
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <i className="bi bi-key text-amber-500"></i>
                  Biens en portefeuille
                  <span className="ml-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[11px] font-bold num">
                    {ownedProperties.length}
                  </span>
                </h2>
                <span className="text-xs text-slate-400">Propriétaire / mandant</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {ownedProperties.map((p) => {
                  const statusMap = { active: 'En ligne', draft: 'Brouillon', sold: 'Vendu', archived: 'Archivé' };
                  return (
                    <li
                      key={p.id}
                      className="px-5 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                      onClick={() => (window.location.href = `/properties/${p.id}/view`)}
                      data-testid={`owned-property-${p.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                        <i className="bi bi-house-door"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{p.titre}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-slate-500 num">Réf. {p.reference}</span>
                          {p.operation && (
                            <span className="text-[11px] text-slate-400">· {String(p.operation).replace(/_/g, ' ')}</span>
                          )}
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${p.status === 'draft' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
                            {statusMap[p.status] || p.status}
                          </span>
                        </div>
                      </div>
                      <i className="bi bi-chevron-right text-slate-300"></i>
                    </li>
                  );
                })}
              </ul>
            </section>
  );

  const sectionBiensProposes = isContactAcquireur(contact) && (
          <section
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
            data-testid="contact-properties"
          >
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <i className="bi bi-buildings text-slate-400"></i>
                Biens proposés
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-bold num">
                  {affectedProperties.length + presentedProperties.length}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => setShowAffectModal(true)}
                data-testid="contact-affect-property-btn"
                className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold flex items-center gap-1.5"
              >
                <i className="bi bi-plus-lg"></i>
                Affecter un bien
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* CARD 1 : Affectés manuellement */}
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <i className="bi bi-pin-angle text-slate-500"></i>
                    Affectés manuellement par l'agent
                  </h3>
                  <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-700 rounded text-[11px] font-bold num">
                    {affectedProperties.length}
                  </span>
                </div>
                {affectedProperties.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Aucun bien affecté</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {affectedProperties.map((presentation, affIdx) => {
                      const { reference, ville, prix, loyer, droitEntree, operation, surface, photo, id } =
                        getBienDisplayData(presentation);
                      const isLocation = ['location', 'location_droit_entree', 'location_gerance'].includes(
                        operation
                      );
                      const presentationId = presentation.id;
                      return (
                        <li key={`aff-${affIdx}`} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start gap-3">
                            <div
                              className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0 overflow-hidden cursor-pointer"
                              onClick={() => (window.location.href = `/properties/${id}/view`)}
                            >
                              {photo ? (
                                <img src={photo} alt={reference} className="w-full h-full object-cover" />
                              ) : (
                                <i className="bi bi-image text-xl"></i>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => (window.location.href = `/properties/${id}/view`)}>
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-[11px] font-bold text-slate-500 num">{reference}</span>
                                {operation && (
                                  <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold uppercase">
                                    {formatOperation(operation)}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {ville}
                                {surface && <span className="num"> · {formatSurfaceFR(surface)}</span>}
                                {isLocation
                                  ? loyer && <span className="num"> · {formatRentFR(loyer)}</span>
                                  : prix && <span className="num"> · {formatPriceFR(prix)}</span>}
                                {droitEntree && (
                                  <span className="text-amber-600 ml-1 num">+{formatPriceFR(droitEntree)}</span>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEmailBien({ id, reference });
                                }}
                                data-testid={`email-affected-${id}`}
                                className="px-2 py-1.5 text-[#0062CC] hover:bg-[#EEF6FE] rounded-lg text-[11px] font-semibold flex items-center gap-1"
                                title="Envoyer un email de proposition pour ce bien"
                              >
                                <i className="bi bi-envelope-paper"></i> Email
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveAffectation(presentationId, reference)}
                                disabled={removingPropertyId === presentationId}
                                data-testid={`remove-affected-${id}`}
                                className="text-slate-400 hover:text-rose-600 p-1"
                                title="Retirer l'affectation"
                              >
                                {removingPropertyId === presentationId ? (
                                  <i className="bi bi-arrow-repeat animate-spin"></i>
                                ) : (
                                  <i className="bi bi-trash text-sm"></i>
                                )}
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* CARD 2 : Présentés */}
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <i className="bi bi-eye text-slate-500"></i>
                    Présentés (bon de visite ou visite effectuée)
                  </h3>
                  <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-700 rounded text-[11px] font-bold num">
                    {presentedProperties.length}
                  </span>
                </div>
                {presentedProperties.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Aucun bien présenté</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {presentedProperties.map((bien, presIdx) => {
                      const { reference, ville, prix, surface, photo, id } = getBienDisplayData(bien);
                      return (
                        <li key={`pres-${presIdx}`} className="p-4 hover:bg-slate-50 cursor-pointer" onClick={() => (window.location.href = `/properties/${id}/view`)}>
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0 overflow-hidden">
                              {photo ? (
                                <img src={photo} alt={reference} className="w-full h-full object-cover" />
                              ) : (
                                <i className="bi bi-image text-xl"></i>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold text-slate-500 num">{reference}</p>
                              <p className="font-semibold text-sm text-slate-900">{ville}</p>
                              <p className="text-xs text-slate-500 mt-0.5 num">
                                {prix ? formatPriceFR(prix) : ''}
                                {surface && ` · ${formatSurfaceFR(surface)}`}
                              </p>
                            </div>
                            <button className="text-slate-400 hover:text-slate-700 p-1" title="Ouvrir">
                              <i className="bi bi-box-arrow-up-right text-xs"></i>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </section>
  );

  const sectionVisites = contact?.id && (
          <section
            className="bg-white rounded-xl shadow-sm border border-slate-200"
            data-testid="contact-visites"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <i className="bi bi-door-open text-slate-400"></i>
                Visites
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-bold num">
                  {visites.length}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => (window.location.href = `/visites/nouvelle?contact=${contact.id}&retour=/contacts/${contact.id}`)}
                data-testid="contact-new-visite-btn"
                className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold flex items-center gap-1.5"
              >
                <i className="bi bi-plus-lg"></i>
                Nouvelle visite
              </button>
            </div>
            {visites.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Aucune visite enregistrée</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {visites.map((v) => (
                  <li
                    key={v.id}
                    onClick={() => (window.location.href = `/visites/${v.id}?retour=/contacts/${contact.id}`)}
                    data-testid={`contact-visite-${v.id}`}
                    className="px-5 py-4 hover:bg-slate-50 cursor-pointer flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900">
                        {v.date_visite ? new Date(v.date_visite).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {(v.biens_resume || []).map((b) => b.reference).join(' · ') || '—'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${
                        v.statut_bon === 'signe'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {v.statut_bon === 'signe' ? 'Bon signé' : 'Bon à joindre'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
  );

  const sectionTaches = contact?.id && (
    <LinkedTasksSection
      contactId={contact.id}
      defaultTaskTitle={`Suivi : ${contact.prenom || ''} ${contact.nom || ''}`.trim()}
    />
  );

  const sectionDocuments = contact?.id && (
          <section
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
            data-testid="contact-documents"
          >
            <DocumentsInternesPanel entityType="contact" entityId={contact.id} />
          </section>
  );

  // lot DE — `limite` : 5 événements sur ordinateur, 4 sur téléphone
  const sectionHistorique = (limite) => (
        <section
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          data-testid="contact-history"
        >
          <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <i className="bi bi-clock-history text-slate-400"></i>
            Historique
            <span className="ml-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[11px] font-bold">
              {history.length}
            </span>
          </h2>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucun historique</p>
          ) : (
            <>
              <ol className="space-y-3 relative border-l-2 border-slate-100 pl-5">
                {(showFullHistory ? history : history.slice(0, limite)).map((event, idx) => {
                  const isExpanded = expandedHistoryItem === idx;
                  // lot DL — le violet est réservé à l'IA : un e-mail écrit à la main est bleu.
                  const color =
                    event.type === 'email_ia_genere'
                      ? '#8b5cf6'
                      : event.type === 'email'
                      ? '#0062CC'
                      : event.type === 'appel'
                      ? '#10b981'
                      : event.type === 'visite'
                      ? '#007BFF'
                      : event.type === 'rdv'
                      ? '#f59e0b'
                      : '#64748b';
                  const content = getEventContent(event);
                  return (
                    <li
                      key={idx}
                      className="relative cursor-pointer"
                      onClick={() => setExpandedHistoryItem(isExpanded ? null : idx)}
                    >
                      <span
                        className="absolute -left-[27px] w-4 h-4 rounded-full border-2 border-white"
                        style={{ backgroundColor: color }}
                      ></span>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-bold text-slate-900">{formatEventTitle(event)}</p>
                        <span className="text-[11px] text-slate-400 num">
                          {event.date ? formatDateFR(event.date) : ''}
                        </span>
                      </div>
                      {content && (
                        <p
                          className={`text-xs text-slate-600 mt-0.5 ${
                            isExpanded ? '' : 'line-clamp-1'
                          }`}
                        >
                          {content}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ol>
              {history.length > limite && (
                <button
                  type="button"
                  onClick={() => setShowFullHistory((v) => !v)}
                  data-testid="contactview-history-all"
                  className="w-full mt-4 text-xs font-semibold text-[#007BFF] hover:bg-blue-50 py-2 rounded-lg transition"
                >
                  {showFullHistory ? 'Réduire' : `Voir tout l'historique (${history.length})`}
                </button>
              )}
            </>
          )}
        </section>
  );

  const modales = (
    <>
      {showTaskModal && contact && (
        <TaskCreateModal
          defaultLinkedContactId={contact.id}
          defaultTitle={`Suivi : ${contact.prenom || ''} ${contact.nom || ''}`.trim()}
          onClose={() => setShowTaskModal(false)}
          onCreated={() => setShowTaskModal(false)}
        />
      )}

      {showEmailModal && contact && (
        <EmailSendModal
          defaultTo={getMainEmail() ? [getMainEmail()] : []}
          defaultSubject=""
          defaultBody={`Bonjour ${displayName},\n\n\n\nCordialement.`}
          contactId={contact.id}
          context="contact"
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {showAIEmailModal && contact && (
        <EmailGeneratorModal
          isOpen={showAIEmailModal}
          contactId={contact.id}
          contactName={displayName}
          contactEmail={getMainEmail()}
          onClose={() => setShowAIEmailModal(false)}
          onEmailSaved={reloadContact}
        />
      )}

      {showAffectModal && contact?.id && (
        <AffectPropertyToContactModal
          contactId={contact.id}
          currentUserId={null}
          onClose={() => setShowAffectModal(false)}
          onSuccess={() => {
            setShowAffectModal(false);
            loadPresentedProperties();
          }}
        />
      )}

      {emailBien && contact?.id && (
        <ProposeBienEmailModal
          contactId={contact.id}
          contactName={`${contact.prenom || ''} ${contact.nom || ''}`.trim()}
          contactEmail={getMainEmail()}
          bienId={emailBien.id}
          bienReference={emailBien.reference}
          onClose={() => setEmailBien(null)}
        />
      )}

      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-800">
                <i className="bi bi-calendar-plus text-slate-500 mr-2"></i>
                Nouvel Événement / RDV
              </h3>
              <button onClick={() => setShowEventModal(false)} className="text-slate-400 hover:text-slate-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-500 text-center italic">
                Fonctionnalité bientôt disponible — utilisez la page Agenda pour créer un RDV pour l'instant.
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ═══════ MODALE : Liste globale des biens qui matchent ═══════ */}
      {showMatchingModal && (
        <div
          className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${telephone ? 'p-0' : 'p-4'}`}
          onClick={() => setShowMatchingModal(false)}
        >
          <div
            className={`bg-white shadow-2xl flex flex-col ${telephone ? 'w-full h-full max-h-full rounded-none' : 'rounded-xl w-full max-w-lg max-h-[85vh]'}`}
            onClick={(e) => e.stopPropagation()}
            data-testid="matching-modal"
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <i className="bi bi-buildings text-blue-500"></i>
                Biens correspondants ({matchingProperties.length})
              </h3>
              <button
                onClick={() => setShowMatchingModal(false)}
                data-testid="matching-modal-close"
                className="text-slate-400 hover:text-slate-600"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2">
              {matchingProperties.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6 italic">
                  Aucun bien ne correspond aux recherches de ce contact.
                </p>
              ) : (
                matchingProperties.map((m, mi) => {
                  const d = getBienDisplayData(m);
                  const isLoc = ['location', 'location_droit_entree', 'location_gerance'].includes(
                    d.operation
                  );
                  const sc = m.score || 0;
                  return (
                    <div
                      key={`mmodal-${mi}`}
                      data-testid={`matching-modal-item-${mi}`}
                      onClick={() => d.id && (window.location.href = `/properties/${d.id}/view`)}
                      className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer transition"
                    >
                      <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0 overflow-hidden">
                        {d.photo ? (
                          <img src={d.photo} alt={d.reference} className="w-full h-full object-cover" />
                        ) : (
                          <i className="bi bi-image"></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-slate-500 num truncate">
                          {d.reference}
                        </p>
                        {/* lot DE n°1 — badges sous le titre : plus de « Brouillon » coupé */}
                        {(d.status === 'draft' || d.overBudget) && (
                          <p className="flex flex-wrap items-center gap-1.5 mt-0.5">
                            {d.status === 'draft' && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-bold uppercase tracking-wide">
                                Brouillon
                              </span>
                            )}
                            {d.overBudget && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-bold uppercase tracking-wide" title="Léger dépassement du budget">
                                +{d.overBudgetPct}% budget
                              </span>
                            )}
                          </p>
                        )}
                        {formatBienType(m.property) && (
                          <p className="text-[11px] font-semibold text-blue-700 truncate">
                            {formatBienType(m.property)}
                          </p>
                        )}
                        <p className="text-xs text-slate-600 num truncate">
                          {d.ville}
                          {d.surface ? ` · ${formatSurfaceFR(d.surface)}` : ''}
                          {isLoc
                            ? d.loyer
                              ? ` · ${formatRentFR(d.loyer)}`
                              : ''
                            : d.prix
                            ? ` · ${formatPriceFR(d.prix)}`
                            : ''}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 px-2 py-1 rounded-md text-xs font-bold num ${
                          sc >= 80
                            ? 'bg-emerald-100 text-emerald-700'
                            : sc >= 50
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {sc}%
                      </span>
                      {renderAffectBtn(d.id, d.reference)}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <ContactCascadeModal
        open={cascadeModal.open}
        mode={cascadeModal.mode}
        contact={contact}
        linkedProperties={cascadeModal.linkedProperties}
        hasActiveSearches={cascadeModal.hasActiveSearches}
        isAcquereur={cascadeModal.isAcquereur}
        isProprietaire={cascadeModal.isProprietaire}
        onClose={() => setCascadeModal((m) => ({ ...m, open: false }))}
        onConfirmArchive={confirmCascadeArchive}
      />
    </>
  );

  // ═══════════ lot DE — rendu TÉLÉPHONE (< 768 px) : quatre onglets ═══════════
  const telPrincipal = getMainPhone();
  const mailPrincipal = getMainEmail();
  const roleLibelle = libelleRole(contact?.type_contact) || contact?.type_contact || '';
  const sousTitre = [roleLibelle, contact?.ville].filter(Boolean).join(' · ');
  const nbBiensLies =
    affectedProperties.length + presentedProperties.length + visites.length + ownedProperties.length;
  const premiereRecherche =
    (contact?.recherches || []).find((r) => r.active !== false) || (contact?.recherches || [])[0] || null;
  const derniereVisite =
    [...visites].sort((a, b) => new Date(b.date_visite || 0) - new Date(a.date_visite || 0))[0] || null;

  const ACTIONS_MENU = [
    { testid: 'contact-task-btn', label: 'Tâche', icone: 'bi-check2-square', action: () => setShowTaskModal(true) },
    { testid: 'contact-rdv-btn', label: 'Rendez-vous', icone: 'bi-calendar-event', action: () => setShowEventModal(true) },
    { testid: 'contact-ai-email-btn', label: "E-mail rédigé par l'IA", icone: 'bi-envelope-paper-heart', action: () => setShowAIEmailModal(true) },
    { testid: 'contact-print-btn', label: 'Imprimer', icone: 'bi-printer', action: handlePrint },
    { testid: 'contact-archive-btn', label: 'Archiver', icone: 'bi-archive-fill', action: handleArchive, couleur: 'text-orange-500' },
    { testid: 'contact-delete-btn', label: 'Supprimer', icone: 'bi-trash-fill', action: handleDelete, couleur: 'text-red-600' },
  ];

  const menuActionsTelephone = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowActionsMenu(!showActionsMenu)}
        data-testid="contact-more-btn"
        aria-label="Plus d'actions"
        className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"
      >
        <i className="bi bi-three-dots-vertical text-lg"></i>
      </button>
      {showActionsMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowActionsMenu(false)}></div>
          <div className="absolute right-0 top-full mt-1 w-60 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
            {ACTIONS_MENU.map((a) => (
              <button
                key={a.testid}
                type="button"
                onClick={() => {
                  setShowActionsMenu(false);
                  a.action();
                }}
                data-testid={a.testid}
                className={`w-full px-4 py-3 text-left text-sm font-medium hover:bg-slate-50 flex items-center gap-3 ${a.couleur || 'text-slate-700'}`}
              >
                <i className={`bi ${a.icone}`}></i> {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const boutonRapide = ({ testid, label, icone, href, onClick, cible, couleur }) => {
    const classes = `h-14 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${couleur}`;
    if (href) {
      return (
        <a href={href} data-testid={testid} target={cible} rel={cible ? 'noopener noreferrer' : undefined} className={classes}>
          <i className={`bi ${icone} text-lg`}></i>
          {label}
        </a>
      );
    }
    return (
      <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid} className={`${classes} disabled:opacity-40`}>
        <i className={`bi ${icone} text-lg`}></i>
        {label}
      </button>
    );
  };

  const enTeteTelephoneReduit = (
    <header className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2">
      <button
        type="button"
        onClick={onClose}
        data-testid="contact-back-btn"
        aria-label="Retour"
        className="w-11 h-11 rounded-xl text-slate-600 flex items-center justify-center shrink-0"
      >
        <i className="bi bi-arrow-left text-lg"></i>
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-900 text-sm leading-tight" data-testid="contact-name">{displayName}</p>
        {sousTitre && <p className="text-xs text-slate-500 truncate">{sousTitre}</p>}
      </div>
      {telPrincipal ? (
        <a
          href={`tel:${telPrincipal}`}
          data-testid="contact-tel-btn"
          aria-label="Appeler"
          className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"
        >
          <i className="bi bi-telephone"></i>
        </a>
      ) : null}
      {menuActionsTelephone}
    </header>
  );

  const enTeteTelephoneComplet = (
    <header className="bg-white border-b border-slate-200 px-4 pt-2 pb-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          data-testid="contact-back-btn"
          aria-label="Retour"
          className="w-11 h-11 rounded-xl text-slate-600 flex items-center justify-center"
        >
          <i className="bi bi-arrow-left text-lg"></i>
        </button>
        {menuActionsTelephone}
      </div>

      <div className="flex items-start gap-3 mt-1">
        <div className="w-14 h-14 rounded-full bg-blue-100 text-[#007BFF] flex items-center justify-center font-bold text-lg shrink-0 overflow-hidden">
          {contact?.photo_url ? (
            <img src={getMediaUrl(contact.photo_url)} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            getContactInitials(contact?.prenom, contact?.nom)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-slate-900 break-words" data-testid="contact-name">
            {displayName}
          </h1>
          <p className="text-sm text-slate-600 break-words">{sousTitre || '—'}</p>
          {(negociateurNom || contact?.negociateur_nom) && (
            <p className="text-xs text-slate-500 mt-0.5" data-testid="contact-suivi-par">
              <i className="bi bi-person-badge mr-1"></i>
              Suivi par {negociateurNom || contact.negociateur_nom}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {contact?.statut_lead && (
              <span
                data-testid="contact-statut-badge"
                className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md text-[11px] font-bold uppercase"
              >
                {contact.statut_lead}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-4">
        {boutonRapide({
          testid: 'contact-tel-btn',
          label: 'Appeler',
          icone: 'bi-telephone',
          href: telPrincipal ? `tel:${telPrincipal}` : null,
          couleur: telPrincipal ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400',
        })}
        {boutonRapide({
          testid: 'contact-wa-btn',
          label: 'WhatsApp',
          icone: 'bi-whatsapp',
          href: telPrincipal ? `https://wa.me/${numeroWhatsapp(telPrincipal)}` : null,
          cible: '_blank',
          couleur: telPrincipal ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400',
        })}
        {boutonRapide({
          testid: 'contact-email-btn',
          label: 'E-mail',
          icone: 'bi-envelope',
          onClick: mailPrincipal ? () => setShowEmailModal(true) : null,
          couleur: mailPrincipal ? 'bg-[#EEF6FE] text-[#0062CC]' : 'bg-slate-100 text-slate-400',
        })}
        {boutonRapide({
          testid: 'contact-visite-btn',
          label: 'Visite',
          icone: 'bi-door-open',
          onClick: () => (window.location.href = `/visites/nouvelle?contact=${contact.id}&retour=/contacts/${contact.id}`),
          couleur: 'bg-slate-100 text-slate-700',
        })}
      </div>
    </header>
  );

  const barreOnglets = (
    <nav
      className="sticky top-0 z-20 bg-white border-b border-slate-200 flex"
      data-testid="contact-tabs"
    >
      {[
        { id: 'resume', label: 'Résumé' },
        { id: 'recherche', label: 'Recherche' },
        { id: 'biens', label: 'Biens', compte: nbBiensLies },
        { id: 'activite', label: 'Activité' },
      ].map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => changerOnglet(o.id)}
          data-testid={`contact-tab-${o.id}`}
          className={`flex-1 h-12 text-sm font-semibold border-b-2 transition ${
            onglet === o.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500'
          }`}
        >
          {o.label}
          {typeof o.compte === 'number' && o.compte > 0 && (
            <span className="ml-1 text-xs num text-slate-400">{o.compte}</span>
          )}
        </button>
      ))}
    </nav>
  );

  // Trois chiffres (téléphone) tirés des indicateurs existants
  const ligneChiffres = isContactAcquireur(contact) && (
    <section className="grid grid-cols-3 gap-2" data-testid="contact-kpis">
      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Faisabilité</p>
        <p className="text-xl font-bold text-slate-900 num mt-0.5">{score === null ? '—' : `${score}/100`}</p>
      </div>
      <button
        type="button"
        onClick={() => matchingCount > 0 && setShowMatchingModal(true)}
        className="bg-white rounded-xl border border-slate-200 p-3 text-center"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Biens qui correspondent</p>
        <p className="text-xl font-bold text-blue-700 num mt-0.5">{matchingCount}</p>
      </button>
      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Dernier échange</p>
        <p className="text-sm font-bold text-slate-900 num mt-1.5">
          {history.length > 0 && history[0].date ? formatDateFR(history[0].date) : '—'}
        </p>
      </div>
    </section>
  );

  const resumeIaTelephone = aiSummary && (
    <section
      className="rounded-xl shadow-md text-white p-4"
      style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4338CA 100%)' }}
      data-testid="contact-ai-summary"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <i className="bi bi-stars text-white"></i>
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-white">Résumé IA</h2>
        {contact?.resume_ia?.stale && (
          <span
            data-testid="ai-summary-stale-badge"
            className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-200 border border-amber-300/40"
          >
            <i className="bi bi-exclamation-triangle-fill mr-1"></i>À actualiser
          </span>
        )}
      </div>
      <p className={`text-sm leading-relaxed text-violet-50 whitespace-pre-wrap ${resumeIaDeplie ? '' : 'line-clamp-3'}`}>
        {aiSummary}
      </p>
      <div className="flex items-center gap-4 mt-3">
        <button
          type="button"
          onClick={() => setResumeIaDeplie((v) => !v)}
          data-testid="contact-ai-summary-more"
          className="text-[11px] text-white font-semibold underline"
        >
          {resumeIaDeplie ? 'Réduire' : 'Lire tout'}
        </button>
        <button
          type="button"
          onClick={handleRegenerateAiSummary}
          disabled={regenerating}
          data-testid="contact-regenerate-ai"
          className="text-[11px] text-violet-200 font-semibold disabled:opacity-50"
        >
          <i className={`bi bi-arrow-clockwise mr-1 ${regenerating ? 'animate-spin' : ''}`}></i>
          {regenerating ? 'Régénération…' : 'Régénérer'}
        </button>
      </div>
    </section>
  );

  const ligneRecherche = isContactAcquireur(contact) && (
    <button
      type="button"
      onClick={() => changerOnglet('recherche')}
      data-testid="contact-search-summary"
      className="w-full bg-white rounded-xl border border-slate-200 p-4 text-left flex items-center gap-3 min-h-[56px]"
    >
      <i className="bi bi-search text-blue-600"></i>
      <span className="flex-1 min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Ce que recherche le client
        </span>
        <span className="block text-sm text-slate-800 truncate">
          {premiereRecherche
            ? [
                formatOperation(premiereRecherche.operation),
                (premiereRecherche.villes || []).join(', ') || (premiereRecherche.secteurs || []).join(', '),
                premiereRecherche.budget_max ? `jusqu'à ${formatPriceFR(premiereRecherche.budget_max)}` : '',
              ]
                .filter(Boolean)
                .join(' · ') || 'Recherche à compléter'
            : 'Aucune recherche enregistrée'}
        </span>
      </span>
      <i className="bi bi-chevron-right text-slate-300"></i>
    </button>
  );

  const blocASuivre = (
    <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="contact-a-suivre">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">À suivre</h2>
      <div className="space-y-2">
        <p className="text-sm text-slate-800 flex items-start gap-2">
          <i className="bi bi-check2-square text-slate-400 mt-0.5"></i>
          <span className="min-w-0">
            {prochaineTache
              ? `${prochaineTache.title}${prochaineTache.due_date ? ` — ${formatDateFR(prochaineTache.due_date)}` : ''}`
              : 'Aucune tâche en cours'}
          </span>
        </p>
        <p className="text-sm text-slate-800 flex items-start gap-2">
          <i className="bi bi-door-open text-slate-400 mt-0.5"></i>
          <span className="min-w-0">
            {derniereVisite
              ? `Dernière visite le ${formatDateFR(derniereVisite.date_visite)}`
              : 'Aucune visite enregistrée'}
          </span>
        </p>
      </div>
    </section>
  );

  const carteRapprochement = isContactAcquireur(contact) && (
    <div
      data-testid="contact-matching-count-card"
      onClick={() => matchingCount > 0 && setShowMatchingModal(true)}
      className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 min-h-[56px]"
    >
      <i className="bi bi-buildings text-blue-600"></i>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">
          <span className="num">{matchingCount}</span> bien{matchingCount > 1 ? 's' : ''} qui correspond
          {matchingCount > 1 ? 'ent' : ''}
        </p>
        <p className="text-[11px] text-blue-600 font-semibold">
          {matchingCount > 0 ? 'Voir les biens' : 'Aucun bien pour l’instant'}
        </p>
      </div>
      <button
        type="button"
        data-testid="contact-refresh-matching-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleRefreshMatching();
        }}
        disabled={refreshingMatching}
        aria-label="Relancer le rapprochement"
        className="w-11 h-11 rounded-xl text-slate-400 flex items-center justify-center disabled:opacity-50"
      >
        <i className={`bi bi-arrow-clockwise ${refreshingMatching ? 'animate-spin' : ''}`}></i>
      </button>
    </div>
  );

  if (telephone) {
    return (
      <div className="bg-slate-50 min-h-screen" data-testid="contact-view-page">
        {onglet === 'resume' ? enTeteTelephoneComplet : enTeteTelephoneReduit}
        {barreOnglets}
        <div className="p-4 space-y-4">
          {onglet === 'resume' && (
            <>
              {ligneChiffres}
              {resumeIaTelephone}
              {blocIdentite}
              {ligneRecherche}
              {blocASuivre}
              {blocSegmentation}
            </>
          )}
          {onglet === 'recherche' && (
            <>
              {sectionRecherches}
              {blocFinancier}
            </>
          )}
          {onglet === 'biens' && (
            <>
              {sectionVisites}
              {sectionBiensProposes}
              {carteRapprochement}
              {isContactMandant(contact) && sectionBiensProprietaire}
            </>
          )}
          {onglet === 'activite' && (
            <>
              {sectionTaches}
              {sectionDocuments}
              {sectionHistorique(4)}
            </>
          )}
        </div>
        {/* le menu du bas de l'application ne doit rien recouvrir (lot DA) */}
        <div style={{ height: 'calc(70px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />
        {modales}
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="contact-view-page">
      <div className="max-w-6xl mx-auto p-4 md:p-6 lg:p-8 space-y-5">
        {enTete}
        {sectionKpis}
        {sectionResumeIA}
        {blocIdentite}
        {blocFinancier}
        {blocSegmentation}
        {sectionRecherches}
        {sectionBiensProprietaire}
        {sectionBiensProposes}
        {sectionVisites}
        {sectionTaches}
        {sectionDocuments}
        {sectionHistorique(5)}
      </div>
      {modales}
    </div>
  );

};

export default ContactViewMode;
